import { Controller } from '@hotwired/stimulus'

/**
 * Redirects the page after form submission, with optional polling for server state changes.
 *
 * Usage:
 * - Simple delay: data-ui-redirect-after-submit-url-value="/redirect/path"
 * - Poll for changes: data-ui-redirect-after-submit-poll-url-value="/resource/path"
 * - Custom delay: data-ui-redirect-after-submit-delay-value="1000"
 *
 * When pollUrl is provided, polls for ETag changes to detect when background jobs update the resource.
 * The polled controller MUST call `fresh_when @resource` for this to work.
 * When no pollUrl, uses simple delay-based redirect (default 500ms).
 *
 * Example: Wait for CMR generation to update transport state before redirecting
 * data: { ui_redirect_after_submit_url_value: "/transfers",
 *         ui_redirect_after_submit_poll_url_value: "/transports/123" }
 */
type RedirectTarget = 'opener' | 'self'

export default class extends Controller<HTMLFormElement> {
  static values = {
    url: String,
    pollUrl: String,
    target: { type: String, default: 'opener' },
    delay: { type: Number, default: 500 }
  }

  declare readonly urlValue: string
  declare readonly pollUrlValue: string
  declare readonly targetValue: RedirectTarget
  declare readonly delayValue: number
  declare readonly hasTargetValue: boolean
  declare readonly hasDelayValue: boolean
  declare readonly hasPollUrlValue: boolean

  private readonly POLL_INTERVAL = 250 // 250ms
  private readonly POLL_TIMEOUT = 5000 // 5 seconds

  async submit(): Promise<void> {
    const target = (this.hasTargetValue ? this.targetValue : 'opener') as RedirectTarget

    if (this.hasPollUrlValue) {
      await this.waitForETagChange()
    } else {
      const delay = this.hasDelayValue ? this.delayValue : 500
      setTimeout(() => this.performRedirect(target), delay)
      return
    }

    this.performRedirect(target)
  }

  private async waitForETagChange(): Promise<void> {
    try {
      // Get initial ETag (controller must call fresh_when for this to work)
      const initialResponse = await fetch(this.pollUrlValue, { method: 'HEAD' })
      const initialETag = initialResponse.headers.get('ETag')

      // Poll for ETag changes
      const startTime = Date.now()
      while (Date.now() - startTime < this.POLL_TIMEOUT) {
        const response = await fetch(this.pollUrlValue, { method: 'HEAD' })
        const currentETag = response.headers.get('ETag')

        if (currentETag !== initialETag) {
          // ETag changed - state was updated
          return
        }

        await new Promise(resolve => setTimeout(resolve, this.POLL_INTERVAL))
      }
    } catch (error) {
      // On any error, just proceed with redirect after a short delay
      await new Promise(resolve => setTimeout(resolve, this.delayValue))
    }
  }

  private performRedirect(target: RedirectTarget): void {
    const redirectUrl = this.urlValue
    if (!redirectUrl) return

    const actions: Record<RedirectTarget, () => void> = {
      opener: () => this.redirectOpener(redirectUrl),
      self: () => this.redirectSelf(redirectUrl)
    }

    actions[target]()
  }

  private redirectOpener(url: string): void {
    if (window.opener) {
      window.opener.location.href = url
      window.close()
    } else {
      this.redirectSelf(url)
    }
  }

  private redirectSelf(url: string): void {
    window.location.href = url
  }
}
